import { Fragment } from 'react';
import { cn } from '@/lib/utils';
import { CellValue } from './CellValue';

interface CountsStripProps {
  counts?: { label: string; value: string }[];
  className?: string;
}

/**
 * `shape: 'counts'` — the one-line summary strip, e.g. `Items 2 · Put away 80 KG`.
 *
 * Deliberately NOT a table. A counts block is a sentence fragment about the whole action, so a two-column
 * grid would give three numbers the visual weight of a breakdown they are not. It wraps rather than
 * truncates; at 364px two or three pairs fit on one line and a fourth drops cleanly.
 *
 * Every value is already formatted and already carries its unit — `80 KG` arrives as that string. The unit
 * belongs to the server precisely because summing across incompatible units was a live bug (E5 #492), and
 * the fix only holds if the client never re-derives one.
 */
export function CountsStrip({ counts, className }: CountsStripProps) {
  const safeCounts = Array.isArray(counts) ? counts : [];

  // Unlike `lines`, an empty counts array carries no information — there is no "zero of nothing" to state.
  if (safeCounts.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs leading-normal', className)}>
      {safeCounts.map((count, index) => (
        <Fragment key={`${count.label}-${index}`}>
          {index > 0 && (
            <span aria-hidden className="text-muted-foreground/50">
              ·
            </span>
          )}
          {/* Label and value never split across lines — the pair is the unit of meaning. */}
          <span className="whitespace-nowrap">
            <span className="text-muted-foreground">{count.label}</span>{' '}
            <CellValue value={count.value} className="font-medium tabular-nums text-foreground" />
          </span>
        </Fragment>
      ))}
    </div>
  );
}
